import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/kfmApi";
import { toast } from "sonner";
import MarkdownLite from "@/components/MarkdownLite";
import { Loader2, Send, Wand2, Sparkles, Trash2, ListChecks, RotateCcw, CalendarRange, Image as ImageIcon, PencilLine, ImagePlus, X, LayoutGrid, Clock, History as HistoryIcon, Activity, FileDown, Video, EyeOff } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SID_KEY = "owp_director_session";

// Human summary of a staged item's destinations (Facebook Direct / Blog / PostPulse platforms).
const destLabel = (item = {}) =>
  [item.fb_direct && "Facebook", item.blog && "Blog", ...(item.platforms || []).map((p) => `PP:${p}`)]
    .filter(Boolean).join(" · ") || (item.platforms || []).join(", ") || "Facebook";

const STARTERS = [
  "Draft the next 14 days of Daily Nuggets for Facebook",
  "Draft a LinkedIn post on finding purpose in ordinary work, then stage it",
  "Review whatever is staged and tell me if it's on-brand",
  "Plan a 3-post mini-campaign on anxiety and peace",
];

export const DirectorChat = ({ onQueueChange, onGoToReview, seed, onSeedConsumed }) => {
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SID_KEY) || "");
  const [messages, setMessages] = useState([]); // {role, text, actions}
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [removing, setRemoving] = useState({});
  const [approving, setApproving] = useState({});
  const [attachedFile, setAttachedFile] = useState(null); // { dataUrl, kind: "image"|"video", name }
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const currentSidRef = useRef(sessionId);
  const skipHistoryRef = useRef(false);

  useEffect(() => { currentSidRef.current = sessionId; }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    // Skip the reload right after send() assigned a brand-new session id — we already
    // hold the optimistic user message and the reply is still being generated.
    if (skipHistoryRef.current) { skipHistoryRef.current = false; return; }
    const sid = sessionId;
    api.get(`/admin/content/director/history?session_id=${sid}`)
      .then(({ data }) => {
        // Ignore a stale response if the session changed / was reset meanwhile.
        if (currentSidRef.current === sid) setMessages(data.transcript || []);
      })
      .catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  // A post rejected/sent-for-edit from the Review queue lands here as a seed: pre-fill the
  // composer with the post's copy so the owner can tell Shee what to change, then send.
  useEffect(() => {
    if (!seed) return;
    setInput(seed);
    onSeedConsumed?.();
    setTimeout(() => {
      const el = document.querySelector('[data-testid="director-input"]');
      if (el) { el.focus(); el.style.height = "auto"; el.style.height = Math.min(el.scrollHeight, 200) + "px"; el.setSelectionRange(el.value.length, el.value.length); }
    }, 120);
  }, [seed]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickAttachment = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isVideo = file.type.startsWith("video/");
    const maxMb = isVideo ? 20 : 8;
    if (file.size > maxMb * 1024 * 1024) { toast.error(`${isVideo ? "Video" : "Image"} is too large (max ${maxMb}MB)`); return; }
    const reader = new FileReader();
    reader.onload = () => setAttachedFile({ dataUrl: reader.result, kind: isVideo ? "video" : "image", name: file.name });
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const send = async (text, opts = {}) => {
    const msg = (text ?? input).trim();
    if ((!msg && !attachedFile) || sending) return;
    const ref = attachedFile;
    setInput("");
    setAttachedFile(null);
    setMessages((m) => [...m, { role: "user", text: msg, actions: [], thumb: ref?.kind === "image" ? ref.dataUrl : null, attachmentKind: ref?.kind, attachmentName: ref?.name }]);
    setSending(true);
    try {
      const { data } = await api.post("/admin/content/director/chat",
        { session_id: currentSidRef.current, message: msg || `(${ref?.kind === "video" ? "video" : "reference image"} attached)`,
          reference_image: ref?.dataUrl || "", roadmap_id: opts.roadmapId || "" });
      if (data.session_id && data.session_id !== sessionId) {
        skipHistoryRef.current = true; // keep the optimistic message; don't reload empty history
        currentSidRef.current = data.session_id;
        setSessionId(data.session_id);
        localStorage.setItem(SID_KEY, data.session_id);
      }
      const jobId = data.job_id;
      if (!jobId) throw new Error("no-job");
      // Shee works in the background (drafting + image generation can take a while), so we
      // poll for her result instead of holding one long request open (which the gateway kills).
      const started = Date.now();
      let result = null;
      while (Date.now() - started < 5 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 2000));
        let s;
        try {
          s = (await api.get(`/admin/content/director/chat/status?job_id=${jobId}`)).data;
        } catch (err) {
          if (err.response?.status === 404) continue; // job doc not visible yet — retry
          throw err;
        }
        if (s.status === "done") { result = s; break; }
        if (s.status === "error") { throw new Error(s.error || "error"); }
      }
      if (!result) throw new Error("timeout");
      setMessages((m) => [...m, { role: "assistant", text: result.reply, actions: result.actions || [] }]);
      if (typeof result.context_pct === "number") setCtxPct(result.context_pct);
      if ((result.actions || []).length) onQueueChange?.();
      loadSessions();
    } catch (e) {
      // Surface the ACTUAL reason so a silent Director is debuggable next time:
      // - backend job error -> s.error (bad key / no credit / rate-limited / overloaded / snag)
      // - network failure    -> axios detail or e.message ("Network Error")
      // - our own timeout     -> friendly timeout copy
      let reason;
      if (e.message === "timeout") {
        reason = "Shee timed out — no reply after 5 minutes. She may still be finishing; try again in a moment.";
      } else if (e.message === "no-job") {
        reason = "Shee didn't start — no job was created on the server. Please try again.";
      } else if (e.response?.data?.detail) {
        reason = formatApiError(e.response.data.detail);
      } else if (e.message && e.message !== "error") {
        reason = e.message;
      } else {
        reason = "Sorry — I couldn't respond just now. Please try again.";
      }
      toast.error(reason);
      setMessages((m) => [...m, { role: "assistant", text: reason, actions: [] }]);
    } finally {
      setSending(false);
    }
  };

  const removeStaged = async (queueId) => {
    setRemoving((r) => ({ ...r, [queueId]: true }));
    try {
      await api.delete(`/admin/social/queue/${queueId}`);
      toast.success("Removed from the queue");
      // Mark it removed in the transcript so the card reflects reality.
      setMessages((m) => m.map((msg) => ({
        ...msg,
        actions: (msg.actions || []).map((a) =>
          a.type === "staged" && a.item?.id === queueId ? { ...a, _removed: true } : a),
      })));
      onQueueChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't remove it");
    } finally {
      setRemoving((r) => ({ ...r, [queueId]: false }));
    }
  };

  const approveStaged = async (item) => {
    if (!item?.id) return;
    setApproving((a) => ({ ...a, [item.id]: true }));
    try {
      await api.post(`/admin/social/queue/${item.id}/approve`, {});
      toast.success("Approved — scheduled to publish");
      setMessages((m) => m.map((msg) => ({
        ...msg,
        actions: (msg.actions || []).map((a) =>
          a.type === "staged" && a.item?.id === item.id ? { ...a, _approved: true } : a),
      })));
      onQueueChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't approve it");
    } finally {
      setApproving((a) => ({ ...a, [item.id]: false }));
    }
  };

  const [sessions, setSessions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [checking, setChecking] = useState(false);
  const [ctxPct, setCtxPct] = useState(0);

  // Start a clean conversation and immediately load a prior session's roadmap so the work
  // continues with zero loss (the real fix for context bloat — not silent trimming).
  const continueFromRoadmap = (roadmapId) => {
    localStorage.removeItem(SID_KEY);
    currentSidRef.current = "";
    skipHistoryRef.current = true;
    setSessionId("");
    setMessages([]);
    setCtxPct(0);
    setShowHistory(false);
    setTimeout(() => send("Let's continue from the roadmap.", { roadmapId }), 60);
  };

  const checkHealth = async () => {
    setChecking(true);
    try {
      const { data } = await api.get("/admin/content/director/health");
      if (data.ok) toast.success(data.message || "Shee's AI is reachable.");
      else toast.error(data.message || "Shee's AI is unavailable right now.", { duration: 6000 });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't run the check.");
    } finally {
      setChecking(false);
    }
  };

  const loadSessions = () =>
    api.get("/admin/content/director/sessions")
      .then(({ data }) => setSessions(data.sessions || []))
      .catch(() => {});

  useEffect(() => { loadSessions(); }, []);

  // "New chat" ARCHIVES the current conversation (keeps it in history) and starts fresh.
  const newChat = () => {
    localStorage.removeItem(SID_KEY);
    currentSidRef.current = "";
    setSessionId("");
    setMessages([]);
    setCtxPct(0);
    setShowHistory(false);
    loadSessions();
  };

  const openSession = (sid) => {
    setShowHistory(false);
    if (sid === sessionId) return;
    localStorage.setItem(SID_KEY, sid);
    currentSidRef.current = sid;
    setSessionId(sid);
  };

  const renameSession = async (sid, current, e) => {
    e.stopPropagation();
    const name = window.prompt("Name this conversation (leave blank to reset to the auto title):", current || "");
    if (name === null) return;
    try {
      await api.put("/admin/content/director/rename", { session_id: sid, title: name.trim() });
      loadSessions();
    } catch (err) {
      toast.error("Couldn't rename that conversation");
    }
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation permanently? This can't be undone.")) return;
    try {
      await api.post("/admin/content/director/reset", { session_id: sid, message: "" });
      if (sid === sessionId) { localStorage.removeItem(SID_KEY); setSessionId(""); setMessages([]); }
      loadSessions();
    } catch (err) {
      toast.error("Couldn't delete that conversation");
    }
  };

  return (
    <div data-testid="director-panel" className="bg-[#0E131F] rounded-2xl border border-[#1E273D] overflow-hidden flex flex-col" style={{ height: "min(72vh, 640px)" }}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#1E273D] bg-[#0E131F]">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-[#10222A] flex items-center justify-center text-[#00E5FF]">
            <Wand2 className="w-4 h-4" strokeWidth={1.5} />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-200">Shee · Creative Director</div>
            <div className="text-[11px] text-slate-500">Discuss, draft, critique &amp; stage — approval stays yours</div>
          </div>
        </div>
        {ctxPct >= 55 && (
          <div data-testid="director-context-meter" title={`Conversation context ~${ctxPct}% — Shee will save a roadmap and hand off near 80%`}
               className="hidden sm:flex items-center gap-1.5 mr-2">
            <span className="text-[10px] text-slate-500">context</span>
            <span className="w-16 h-1.5 rounded-full bg-[#141B29] overflow-hidden">
              <span className="block h-full rounded-full transition-all"
                    style={{ width: `${Math.min(100, ctxPct)}%`, background: ctxPct >= 80 ? "#d97706" : "#00E5FF" }} />
            </span>
            <span className="text-[10px] text-slate-500">{ctxPct}%</span>
          </div>
        )}
        <div className="flex items-center gap-1 relative">
          <div className="relative">
            <button data-testid="director-history-btn" onClick={() => { setShowHistory((v) => !v); loadSessions(); }}
                    className="relative z-30 text-xs text-slate-500 hover:text-[#00E5FF] flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-[#141B29]">
              <HistoryIcon className="w-3.5 h-3.5" /> History
            </button>
            {showHistory && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowHistory(false)} />
                <div data-testid="director-history-menu"
                     className="absolute right-0 mt-1 w-72 max-h-80 overflow-y-auto bg-[#0E131F] border border-[#29344D] rounded-xl shadow-lg z-20 py-1">
                  {sessions.length === 0 ? (
                    <div className="px-3 py-4 text-xs text-slate-500 text-center">No past conversations yet.</div>
                  ) : sessions.map((s) => (
                    <div key={s.session_id} data-testid={`director-history-item-${s.session_id}`}
                         onClick={() => openSession(s.session_id)}
                         className={`group flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-[#0E131F] ${s.session_id === sessionId ? "bg-[#10222A]" : ""}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-slate-300 truncate">{s.title}</div>
                        <div className="text-[10px] text-slate-500 mt-0.5">
                          {s.count} message{s.count === 1 ? "" : "s"}
                          {s.updated_at ? ` · ${new Date(s.updated_at).toLocaleDateString([], { month: "short", day: "numeric" })}` : ""}
                        </div>
                      </div>
                      <button data-testid={`director-history-rename-${s.session_id}`}
                              onClick={(e) => renameSession(s.session_id, s.custom ? s.title : "", e)}
                              className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-[#00E5FF] shrink-0 mt-0.5"
                              aria-label="Rename conversation">
                        <PencilLine className="w-3.5 h-3.5" />
                      </button>
                      <button data-testid={`director-history-delete-${s.session_id}`}
                              onClick={(e) => deleteSession(s.session_id, e)}
                              className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-500 shrink-0 mt-0.5"
                              aria-label="Delete conversation">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          <button data-testid="director-health-check" onClick={checkHealth} disabled={checking}
                  title="Check whether Shee's AI engine is reachable"
                  className="text-xs text-slate-500 hover:text-[#00E5FF] flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-[#141B29] disabled:opacity-50">
            {checking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Activity className="w-3.5 h-3.5" />} Check Shee
          </button>
          <button data-testid="director-reset" onClick={newChat}
                  className="text-xs text-slate-500 hover:text-[#00E5FF] flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-[#141B29]">
            <RotateCcw className="w-3.5 h-3.5" /> New chat
          </button>
        </div>
      </div>

      {/* Transcript */}
      <div ref={scrollRef} data-testid="director-transcript" className="flex-1 overflow-y-auto px-4 py-5 space-y-4">
        {messages.length === 0 && !sending && (
          <div className="max-w-md mx-auto text-center pt-6">
            <div className="w-12 h-12 rounded-2xl bg-[#10222A] flex items-center justify-center text-[#00E5FF] mx-auto">
              <Sparkles className="w-6 h-6" strokeWidth={1.5} />
            </div>
            <p className="text-xl text-slate-200 mt-4">Let&rsquo;s shape something.</p>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed">
              Talk through a campaign, hand me a brief for a full package, or ask me to critique what&rsquo;s staged.
              When something&rsquo;s ready, I&rsquo;ll drop it into your Queue &mdash; you always approve before anything goes live.
            </p>
            <div className="mt-5 space-y-2 text-left">
              {STARTERS.map((s, i) => (
                <button key={i} data-testid={`director-starter-${i}`} onClick={() => send(s)}
                        className="w-full text-left text-sm text-slate-400 bg-[#07090E] hover:bg-[#10222A] hover:text-[#00E5FF] border border-[#1E273D] rounded-xl px-4 py-2.5 transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} data-testid={`director-msg-${m.role}-${i}`} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
            <div className={m.role === "user"
              ? "max-w-[85%] bg-[#00E5FF] text-[#07090E] rounded-2xl rounded-br-sm px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap"
              : "max-w-[92%] w-full"}>
              {m.role === "user"
                ? (
                  <>
                    {(m.thumb || m.has_image) && (
                      <img src={m.thumb || undefined} alt="reference"
                           className="mb-2 rounded-lg max-h-40 w-auto object-cover border border-white/30"
                           style={{ display: m.thumb ? "block" : "none" }} />
                    )}
                    {m.attachmentKind === "video" && (
                      <div className="mb-2 inline-flex items-center gap-1.5 bg-white/15 rounded-lg px-2.5 py-1.5 text-xs">
                        <Video className="w-3.5 h-3.5" /> {m.attachmentName || "video attached"}
                      </div>
                    )}
                    {(m.thumb || m.has_image) && !m.text && <span className="opacity-80 italic">reference image attached</span>}
                    {m.text}
                  </>
                )
                : (
                  <div className="bg-[#0E131F] border border-[#1E273D] rounded-2xl rounded-bl-sm px-4 py-3">
                    <div className="text-sm text-slate-300 director-md"><MarkdownLite text={m.text} /></div>
                    {(m.actions || []).filter((a) => a.type === "staged").map((a, j) => (
                      <StagedCard key={j} action={a} removing={!!removing[a.item?.id]}
                                  approving={!!approving[a.item?.id]}
                                  onApprove={() => approveStaged(a.item)}
                                  onRemove={() => removeStaged(a.item?.id)} onGoToReview={onGoToReview} />
                    ))}
                    {(m.actions || []).filter((a) => a.type === "campaign").map((a, j) => (
                      <CampaignCard key={`c${j}`} plan={a.plan} onGoToReview={onGoToReview} />
                    ))}
                    {(m.actions || []).filter((a) => a.type === "image").map((a, j) => (
                      <ImageCard key={`i${j}`} action={a} onGoToReview={onGoToReview} />
                    ))}
                    {(m.actions || []).filter((a) => a.type === "shapes").map((a, j) => (
                      <ShapesCard key={`sh${j}`} action={a} onGoToReview={onGoToReview} />
                    ))}
                    {(m.actions || []).filter((a) => a.type === "rescheduled").map((a, j) => (
                      <RescheduleCard key={`rs${j}`} action={a} onGoToReview={onGoToReview} />
                    ))}
                    {(m.actions || []).filter((a) => a.type === "caption").map((a, j) => (
                      <CaptionCard key={`cap${j}`} action={a} onGoToReview={onGoToReview} />
                    ))}
                    {(m.actions || []).filter((a) => a.type === "handoff").map((a, j) => (
                      <HandoffCard key={`h${j}`} action={a} onContinue={() => continueFromRoadmap(a.roadmap_id)} />
                    ))}
                    {(m.actions || []).filter((a) => a.type === "unpublished").map((a, j) => (
                      <div key={`up${j}`} data-testid={`director-unpublished-${a.slug}`}
                           className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 flex items-center gap-2">
                        <EyeOff className="w-3.5 h-3.5 text-amber-300 shrink-0" />
                        <span className="text-sm text-amber-200">Unpublished &ldquo;{a.title}&rdquo; — off the public site, reversible in Setup.</span>
                      </div>
                    ))}
                    {(m.actions || []).some((a) => a.type === "removed") && (
                      <div className="mt-2 text-[11px] text-slate-500 flex items-center gap-1">
                        <Trash2 className="w-3 h-3" /> Removed from the queue.
                      </div>
                    )}
                  </div>
                )}
            </div>
          </div>
        ))}

        {sending && (
          <div className="flex justify-start" data-testid="director-thinking">
            <div className="bg-[#0E131F] border border-[#1E273D] rounded-2xl rounded-bl-sm px-4 py-3 text-sm text-slate-500 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Thinking…
            </div>
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-[#1E273D] p-3">
        {attachedFile && (
          <div data-testid="director-attachment" className="mb-2 inline-flex items-center gap-2 bg-[#10222A] rounded-lg p-1.5 pr-2">
            {attachedFile.kind === "image"
              ? <img src={attachedFile.dataUrl} alt="attachment" className="w-10 h-10 rounded object-cover" />
              : <div className="w-10 h-10 rounded bg-[#141B29] flex items-center justify-center text-[#00E5FF]"><Video className="w-4 h-4" /></div>}
            <span className="text-[11px] text-slate-400 max-w-[160px] truncate">{attachedFile.kind === "video" ? (attachedFile.name || "Video attached") : "Reference image"}</span>
            <button data-testid="director-attachment-remove" onClick={() => setAttachedFile(null)}
                    aria-label="Remove attachment" className="text-slate-500 hover:text-red-500">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        <div className="flex items-end gap-2">
          <input ref={fileRef} type="file" accept="image/*,video/*" onChange={pickAttachment} className="hidden" data-testid="director-file-input" />
          <button data-testid="director-attach" onClick={() => fileRef.current?.click()}
                  aria-label="Attach an image or video" title="Attach an image or video"
                  className="h-11 w-11 shrink-0 rounded-xl border border-[#29344D] text-slate-500 hover:text-[#00E5FF] hover:border-[#00E5FF] flex items-center justify-center transition-colors">
            <ImagePlus className="w-4 h-4" />
          </button>
          <textarea
            data-testid="director-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onInput={(e) => { e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 200) + "px"; }}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={3}
            placeholder="Talk to Shee, your Creative Director…  (Shift+Enter for a new line)"
            className="flex-1 resize-none rounded-xl border border-[#29344D] px-4 py-3 text-sm leading-relaxed focus:outline-none focus:border-[#00E5FF] min-h-[76px] max-h-[200px] overflow-y-auto"
          />
          <button data-testid="director-send" onClick={() => send()} disabled={sending || (!input.trim() && !attachedFile)}
                  aria-label="Send message to Shee, the Creative Director"
                  className="h-11 w-11 shrink-0 rounded-xl bg-[#00E5FF] hover:bg-[#00B8CC] disabled:opacity-40 text-[#07090E] flex items-center justify-center transition-colors">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
        <p className="text-[10px] text-slate-500 mt-1.5 px-1">Attach a photo or video — ask Shee to actually look at it, or use a photo as a style reference · Staged posts wait in the Queue — nothing publishes without your approval.</p>
      </div>
    </div>
  );
};

const HandoffCard = ({ action, onContinue }) => (
  <div data-testid={`director-handoff-${action.roadmap_id}`}
       className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3">
    <div className="text-[10px] uppercase tracking-widest text-amber-300 flex items-center gap-1">
      <FileDown className="w-3 h-3" /> Roadmap saved · context ~{action.context_pct || 80}%
    </div>
    <div className="text-sm font-medium text-slate-200 mt-0.5">{action.title || "Roadmap"}</div>
    <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">
      This conversation is getting long. I&rsquo;ve saved everything we&rsquo;ve decided so nothing is lost —
      continue in a fresh chat with a clean, fast context.
    </p>
    <div className="flex items-center gap-3 mt-2">
      <a data-testid={`director-roadmap-download-${action.roadmap_id}`}
         href={`${BACKEND}/api/admin/content/director/roadmap/${action.roadmap_id}.md`}
         className="text-xs text-[#00E5FF] hover:underline font-medium">Download roadmap (.md)</a>
      <button data-testid={`director-handoff-continue-${action.roadmap_id}`} onClick={onContinue}
              className="h-8 px-3 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-xs font-medium">
        Start fresh chat &amp; continue
      </button>
    </div>
  </div>
);

const StagedCard = ({ action, removing, approving, onApprove, onRemove, onGoToReview }) => {
  const item = action.item || {};
  const removed = action._removed;
  const approved = action._approved;
  const isNugget = item.kind === "nugget";
  const nug = item.nugget || {};
  const fmtDate = (d) => {
    if (!d) return "";
    try {
      if (d.length > 10 && d.includes("T")) {
        return new Date(d).toLocaleString("en-GB", { timeZone: "Europe/London", weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) + " UK";
      }
      return new Date(d.slice(0, 10) + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", month: "short", day: "numeric" });
    } catch { return d; }
  };
  const img = !isNugget && item.slug ? `${BACKEND}/api/blog/${item.slug}/post-image.png` : null;

  if (isNugget) {
    return (
      <div data-testid={`director-nugget-${item.id}`}
           className={`mt-3 rounded-xl border p-3 ${removed ? "border-[#1E273D] opacity-60" : approved ? "border-green-500/30 bg-green-500/10" : "border-[#00E5FF]/30 bg-[#10222A]/40"}`}>
        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] uppercase tracking-widest text-[#00E5FF] flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Daily Nugget
          </div>
          <div className="text-[11px] text-slate-400">{fmtDate(item.scheduled_time)} · {destLabel(item)}</div>
        </div>
        {nug.verse && <p className="text-[13px] italic text-slate-300 mt-1.5 leading-snug">&ldquo;{nug.verse}&rdquo;</p>}
        {nug.reference && <div className="text-[10px] uppercase tracking-wide text-[#00E5FF] mt-0.5">{nug.reference}</div>}
        {nug.catch && <p className="text-[13px] text-slate-400 mt-2 leading-snug">{nug.catch}</p>}
        {nug.question && <p className="text-[13px] font-medium text-slate-200 mt-1.5 leading-snug">{nug.question}</p>}
        {!removed && (
          <div className="flex items-center gap-3 mt-2.5 pt-2 border-t border-[#1E273D]">
            {approved ? (
              <span data-testid={`director-nugget-approved-${item.id}`} className="text-xs text-green-400 font-medium">&#10003; Approved &middot; scheduled</span>
            ) : (
              <button data-testid={`director-nugget-approve-${item.id}`} onClick={onApprove} disabled={approving}
                      className="text-xs font-medium text-green-400 hover:text-green-300 flex items-center gap-1 disabled:opacity-50">
                {approving ? <Loader2 className="w-3 h-3 animate-spin" /> : <ListChecks className="w-3 h-3" />} Approve
              </button>
            )}
            <button data-testid={`director-nugget-review-${item.id}`} onClick={onGoToReview}
                    className="text-xs text-[#00E5FF] hover:underline font-medium">Open in Queue</button>
            {!approved && (
              <button data-testid={`director-nugget-undo-${item.id}`} onClick={onRemove} disabled={removing}
                      className="text-xs text-slate-500 hover:text-red-500 flex items-center gap-1 disabled:opacity-50">
                {removing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />} Remove
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid={`director-staged-${item.id}`}
         className={`mt-3 rounded-xl border p-3 flex gap-3 ${removed ? "border-[#1E273D] opacity-60" : "border-[#00E5FF]/30 bg-[#10222A]/40"}`}>
      {img && !removed && (
        <img src={img} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0 bg-[#141B29]"
             onError={(e) => { e.currentTarget.style.display = "none"; }} />
      )}
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest text-[#00E5FF] flex items-center gap-1">
          <ListChecks className="w-3 h-3" /> {removed ? "Removed" : "Staged for review"}
        </div>
        <div className="text-sm font-medium text-slate-200 truncate mt-0.5">{item.title || "Social post"}</div>
        <div className="text-[11px] text-slate-400 mt-0.5">{destLabel(item)}</div>
        {!removed && (
          <div className="flex items-center gap-3 mt-2">
            <button data-testid={`director-view-${item.id}`} onClick={onGoToReview}
                    className="text-xs text-[#00E5FF] hover:underline font-medium">Open in Queue</button>
            <button data-testid={`director-undo-${item.id}`} onClick={onRemove} disabled={removing}
                    className="text-xs text-slate-500 hover:text-red-500 flex items-center gap-1 disabled:opacity-50">
              {removing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />} Undo
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const CampaignCard = ({ plan, onGoToReview }) => {
  const dates = plan?.dates || [];
  const fmt = (d) => { try { return new Date(d + "T00:00:00").toLocaleDateString([], { month: "short", day: "numeric" }); } catch { return d; } };
  return (
    <div data-testid={`director-campaign-${plan?.campaign_id || "x"}`}
         className="mt-3 rounded-xl border border-[#00E5FF]/30 bg-[#10222A]/40 p-3">
      <div className="text-[10px] uppercase tracking-widest text-[#00E5FF] flex items-center gap-1">
        <CalendarRange className="w-3 h-3" /> Campaign staged
      </div>
      <div className="text-sm font-medium text-slate-200 mt-0.5">
        {plan?.count} posts · {(plan?.platforms || []).join(", ")}
      </div>
      <div className="text-[11px] text-slate-400 mt-1">
        {dates.length > 0 && <>Scheduled: {fmt(dates[0])}{dates.length > 1 ? ` → ${fmt(dates[dates.length - 1])}` : ""}</>}
      </div>
      <p className="text-[11px] text-slate-500 mt-1.5">Generating in the background — each lands queued for your approval. Nothing publishes automatically.</p>
      <button data-testid={`director-campaign-review-${plan?.campaign_id || "x"}`} onClick={onGoToReview}
              className="text-xs text-[#00E5FF] hover:underline font-medium mt-2">Open Queue</button>
    </div>
  );
};

const ImageCard = ({ action, onGoToReview }) => (
  <div data-testid={`director-image-${action.slug}`}
       data-image-version={(action.image_url || "").split("v=")[1] || ""}
       className="mt-3 rounded-xl border border-[#00E5FF]/30 bg-[#10222A]/40 p-3 flex gap-3">
    {action.image_url && (
      <img src={action.image_url} alt="" className="w-20 h-20 rounded-lg object-cover shrink-0 bg-[#141B29]"
           onError={(e) => { e.currentTarget.style.display = "none"; }} />
    )}
    <div className="min-w-0 flex-1">
      <div className="text-[10px] uppercase tracking-widest text-[#00E5FF] flex items-center gap-1">
        <ImageIcon className="w-3 h-3" /> New photo
      </div>
      {action.image_prompt && (
        <div className="text-[11px] text-slate-400 mt-1 line-clamp-3">{action.image_prompt}</div>
      )}
      <button data-testid={`director-image-review-${action.slug}`} onClick={onGoToReview}
              className="text-xs text-[#00E5FF] hover:underline font-medium mt-2">Open in Queue</button>
    </div>
  </div>
);

const CaptionCard = ({ action, onGoToReview }) => {
  const caps = action.captions || {};
  return (
    <div data-testid={`director-caption-${action.slug}`}
         className="mt-3 rounded-xl border border-[#00E5FF]/30 bg-[#10222A]/40 p-3">
      <div className="text-[10px] uppercase tracking-widest text-[#00E5FF] flex items-center gap-1">
        <PencilLine className="w-3 h-3" /> Caption revised
      </div>
      <div className="mt-1.5 space-y-2">
        {Object.entries(caps).map(([plat, text]) => (
          <div key={plat}>
            <div className="text-[10px] uppercase tracking-wide text-slate-500">{plat}</div>
            <div className="text-[13px] text-slate-300 whitespace-pre-wrap leading-snug">{text}</div>
          </div>
        ))}
      </div>
      <button data-testid={`director-caption-review-${action.slug}`} onClick={onGoToReview}
              className="text-xs text-[#00E5FF] hover:underline font-medium mt-2">Open in Queue</button>
    </div>
  );
};

const ShapesCard = ({ action, onGoToReview }) => {
  const shapes = action.shapes || {};
  const labels = { square: "Square · IG", landscape: "Landscape · FB/LI", portrait: "Portrait · TikTok" };
  const ratio = { square: "aspect-square", landscape: "aspect-video", portrait: "aspect-[9/16]" };
  const order = ["square", "landscape", "portrait"].filter((s) => shapes[s]);
  return (
    <div data-testid={`director-shapes-${action.slug}`}
         className="mt-3 rounded-xl border border-[#00E5FF]/30 bg-[#10222A]/40 p-3">
      <div className="text-[10px] uppercase tracking-widest text-[#00E5FF] flex items-center gap-1">
        <LayoutGrid className="w-3 h-3" /> Shape preview
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2">
        {order.map((s) => (
          <div key={s}>
            <div className={`${ratio[s]} rounded-lg overflow-hidden bg-[#141B29] border border-[#29344D]`}>
              <img src={shapes[s]} alt={s} className="w-full h-full object-cover"
                   onError={(e) => { e.currentTarget.style.display = "none"; }} />
            </div>
            <div className="text-[9px] uppercase tracking-wide text-slate-400 mt-1 text-center">{labels[s]}</div>
          </div>
        ))}
      </div>
      <button data-testid={`director-shapes-review-${action.slug}`} onClick={onGoToReview}
              className="text-xs text-[#00E5FF] hover:underline font-medium mt-2">Open in Queue</button>
    </div>
  );
};

const RescheduleCard = ({ action, onGoToReview }) => {
  const when = action.item?.scheduled_time;
  const fmt = (d) => { try { return d ? new Date(d).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "now / today"; } catch { return d; } };
  return (
    <div data-testid={`director-rescheduled-${action.slug}`}
         className="mt-3 rounded-xl border border-[#00E5FF]/30 bg-[#10222A]/40 p-3">
      <div className="text-[10px] uppercase tracking-widest text-[#00E5FF] flex items-center gap-1">
        <Clock className="w-3 h-3" /> Rescheduled
      </div>
      <div className="text-sm text-slate-200 mt-0.5">New time: {fmt(when)}</div>
      <button data-testid={`director-rescheduled-review-${action.slug}`} onClick={onGoToReview}
              className="text-xs text-[#00E5FF] hover:underline font-medium mt-2">Open in Queue</button>
    </div>
  );
};

export default DirectorChat;